import React from 'react';
import { Flex, Tag, Typography } from '@duxton-web/ui';

import type { GetScorecardResponse } from '../../types';
import {
  calculateDaysDiff,
  formatAbsoluteTime,
  formatFullDate,
  formatRelativeTime,
  getHealthConfig,
  getMetricLevelTag,
} from '../../utils';

interface Props {
  data: GetScorecardResponse;
}

const HealthCard: React.FC<Props> = ({ data }) => {
  const config = getHealthConfig(data.health.overall, data.lastRunAt);

  const metricsSummary = data.metrics
    .map(
      (m) =>
        `${getMetricLevelTag(m.metricLevel).label}: ${m.metricDisplayName}`,
    )
    .join(' · ');

  // Build multi-segment progress bar
  const progressSegments = (() => {
    const { successCount, inProgressCount, failedCount, totalCount } =
      data.health;
    if (totalCount === 0) return [];

    const segments: Array<{ percent: number; color: string }> = [];

    if (successCount > 0) {
      segments.push({
        percent: (successCount / totalCount) * 100,
        color: '#52c41a', // green for success
      });
    }

    if (inProgressCount > 0) {
      const queued = data.metrics.filter(
        (m) => m.status === 'MIDAS_QUEUED',
      ).length;
      const processing = data.metrics.filter(
        (m) => m.status === 'SCORECARD_PROCESSING',
      ).length;

      if (queued > 0) {
        segments.push({
          percent: (queued / totalCount) * 100,
          color: '#faad14', // yellow for queued
        });
      }
      if (processing > 0) {
        segments.push({
          percent: (processing / totalCount) * 100,
          color: '#1890ff', // blue for processing
        });
      }

      const notStarted = inProgressCount - queued - processing;
      if (notStarted > 0) {
        segments.push({
          percent: (notStarted / totalCount) * 100,
          color: '#faad14', // yellow for not started
        });
      }
    }

    if (failedCount > 0) {
      segments.push({
        percent: (failedCount / totalCount) * 100,
        color: '#ff4d4f', // red for failed
      });
    }

    return segments;
  })();

  return (
    <div
      style={{
        padding: '16px 20px',
        background: config.bgColor,
        borderRadius: 8,
        border: `1px solid ${config.borderColor}`,
        marginBottom: 16,
      }}
    >
      {/* Header */}
      <Flex align="center" gap={12} style={{ marginBottom: 6 }}>
        <Tag
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '3px 10px',
            borderRadius: 10,
            background: 'white',
            color: config.dotColor,
            border: `1px solid ${config.borderColor}`,
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: config.dotColor,
              flexShrink: 0,
            }}
          />
          {config.label}
        </Tag>
        <Typography.Text strong style={{ fontSize: 15 }}>
          Scorecard Health
        </Typography.Text>
      </Flex>

      {/* Tracking text */}
      <Typography.Text
        type="secondary"
        style={{ display: 'block', marginBottom: 16 }}
      >
        Tracking {data.metrics.length} metrics: {metricsSummary}
      </Typography.Text>

      {/* Stats row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
          gap: 24,
          paddingTop: 16,
          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        {/* Coverage */}
        <Flex vertical>
          <Typography.Text
            type="secondary"
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            COVERAGE
          </Typography.Text>
          <Typography.Text strong style={{ fontSize: 14, lineHeight: 1.4 }}>
            {data.coverage.startDate && data.coverage.endDate
              ? `${formatFullDate(data.coverage.startDate)} → ${formatFullDate(data.coverage.endDate)}`
              : `Starts ${formatFullDate(data.coverage.startDate)}`}
          </Typography.Text>
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, marginTop: 2 }}
          >
            {data.coverage.endDate
              ? `${calculateDaysDiff(data.coverage.startDate, data.coverage.endDate).toLocaleString()} days`
              : 'First batch runs overnight'}
          </Typography.Text>
        </Flex>

        {/* Metric Health */}
        <Flex vertical>
          <Typography.Text
            type="secondary"
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            METRIC HEALTH
          </Typography.Text>
          <Typography.Text strong style={{ fontSize: 14, lineHeight: 1.4 }}>
            {data.health.successCount} / {data.health.totalCount} successful
          </Typography.Text>
          {/* Multi-segment progress bar */}
          <Flex
            style={{
              height: 6,
              borderRadius: 3,
              overflow: 'hidden',
              background: 'rgba(0, 0, 0, 0.06)',
              marginTop: 8,
              width: '100%',
              maxWidth: 160,
            }}
          >
            {progressSegments.map((seg, idx) => (
              <div
                key={idx}
                style={{
                  width: `${seg.percent}%`,
                  background: seg.color,
                }}
              />
            ))}
          </Flex>
        </Flex>

        {/* Last Run */}
        <Flex vertical>
          <Typography.Text
            type="secondary"
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            LAST RUN
          </Typography.Text>
          <Typography.Text strong style={{ fontSize: 14, lineHeight: 1.4 }}>
            {formatRelativeTime(data.lastRunAt)}
          </Typography.Text>
          <Typography.Text
            type="secondary"
            style={{
              fontSize: 12,
              marginTop: 2,
              textTransform: 'capitalize',
            }}
          >
            {formatAbsoluteTime(data.lastRunAt)}
          </Typography.Text>
        </Flex>

        {/* Next Run */}
        <Flex vertical>
          <Typography.Text
            type="secondary"
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            NEXT RUN
          </Typography.Text>
          <Typography.Text strong style={{ fontSize: 14, lineHeight: 1.4 }}>
            {formatRelativeTime(data.nextRunAt)}
          </Typography.Text>
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, marginTop: 2 }}
          >
            {formatAbsoluteTime(data.nextRunAt)}
          </Typography.Text>
        </Flex>
      </div>
    </div>
  );
};

export default HealthCard;
